import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormInstance } from 'antd';
import { datetime } from '@common/services/datetime';
import {
  Gap,
  Icon,
  InputNumber,
  LoadingState,
  Form,
  Table,
  Col,
  Row,
} from '@common/components';
import { useMutation, useNavigate, useQuery } from '@common/hooks';
import { buildCloseAllDialogsUrl } from '@common/services';
import { toHumanTimeFormat } from 'works/legacy/lib/date-helper';
import { createMessage } from 'works/legacy/lib/message-helper';
import OrganizationUnitSelect from 'works/legacy/components/organization-unit-select/organization-unit-select';
import { RoleSelect } from '@clinician-profile/components';
import { RolesQuery } from '@clinician-profile/graphql';
import {
  CLEAR_WORKLOAD_MUTATION,
  SCHEDULE_DAY_BOUNDARY_QUERY,
  SHIFT_GAPS_BY_DAY,
  WORKLOAD_BLOCKS_QUERY,
} from '@scheduling/graphql';
import { ConflictShiftsBanner } from './_conflict-shifts-banner';
import { NoShiftConfigsWarning } from './_no-shift-configs-warning';
import { ShiftConfigsInfo } from './_shift-configs-info';
import { useShiftConfigCheck } from './_use-shift-config-check';

const COLUMNS = [
  { title: 'Hours', dataIndex: 'hours', key: 'hours' },
  { title: 'Needs', dataIndex: 'needs', key: 'needs' },
  { title: 'Status', dataIndex: 'status', key: 'status' },
];

export type FormStateType = {
  canSave: boolean;
  loading: boolean;
  hasExistingBlocks: boolean;
  onClear: () => void;
};

export type FormValuesType = {
  organizationUnitId: string;
  roleId: string;
  boundaryHour: number;
  dayHeadcount: number;
  nightHeadcount: number;
  shiftGapIdsToCancel: string[];
};

export function SetWorkloadForm({
  form,
  date,
  onStateChange,
  onContinue,
}: {
  form: FormInstance;
  date?: string;
  onStateChange: (state: FormStateType) => void;
  onContinue: (values: FormValuesType) => void;
}) {
  const navigate = useNavigate();
  const [organizationUnitId, setOrganizationUnitId] = useState<string | null>(
    null
  );
  const [roleId, setRoleId] = useState<string | null>(null);
  const [boundaryHour, setBoundaryHour] = useState<number | null>(null);
  const [shiftGapIdsToKeep, setShiftGapIdsToKeep] = useState<Set<string>>(
    new Set()
  );

  const hasUnitAndRole = !!organizationUnitId && !!roleId;

  const { unitHasAnyConfigs, hasApplicableConfigs, shiftConfigs } =
    useShiftConfigCheck({
      organizationUnitId,
      roleId,
    });
  const configCheckComplete =
    organizationUnitId !== null ? unitHasAnyConfigs !== null : true;
  const showWarning =
    unitHasAnyConfigs === false ||
    (hasUnitAndRole && hasApplicableConfigs === false);

  const nightHour = boundaryHour !== null ? (boundaryHour + 12) % 24 : null;

  const queryRange = useMemo(
    () =>
      boundaryHour !== null
        ? {
            startsAt: utcDateAtHour(date, boundaryHour).format(),
            endsAt: utcDateAtHour(date, boundaryHour, 1).format(),
          }
        : null,
    [date, boundaryHour]
  );

  const canSave = hasUnitAndRole && boundaryHour !== null && !showWarning;

  const { loading: rolesLoading, data: rolesData } = useQuery(RolesQuery, {
    variables: { organizationUnitId: organizationUnitId ?? undefined },
    fetchPolicy: 'cache-and-network',
  });

  const { loading: boundaryLoading } = useQuery(SCHEDULE_DAY_BOUNDARY_QUERY, {
    variables: { orgUnitId: organizationUnitId! },
    skip: !organizationUnitId,
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const value = data?.organizationUnit?.scheduleDayBoundary;
      if (value) setBoundaryHour(datetime(value, 'HH:mm').hour());
    },
  });

  const { loading: workloadLoading, data: workloadData } = useQuery(
    WORKLOAD_BLOCKS_QUERY,
    {
      variables: {
        organizationUnitId: organizationUnitId!,
        roleId: roleId!,
        startsAt: queryRange?.startsAt ?? '',
        endsAt: queryRange?.endsAt ?? '',
      },
      skip: !hasUnitAndRole || !queryRange,
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const blocks = data?.workloadBlocks?.blocks || [];
        form.setFieldsValue({
          dayHeadcount: blocks[0]?.headcount ?? 0,
          nightHeadcount: blocks[1]?.headcount ?? 0,
        });
      },
    }
  );

  const { data: shiftGapsData } = useQuery(SHIFT_GAPS_BY_DAY, {
    variables: {
      date: date ?? '',
      filters: {
        unitIds: [organizationUnitId!],
        roleIds: [roleId!],
        sources: ['internal', 'internal_import'],
      },
      offset: 0,
      limit: 100,
      states: ['open'],
    },
    skip: !hasUnitAndRole,
    fetchPolicy: 'network-only',
  });

  const manualShiftGaps = shiftGapsData?.shiftGapsByDay?.shiftGaps ?? [];

  const blocks = workloadData?.workloadBlocks?.blocks || [];
  const hasExistingBlocks = blocks.some((b) => (b.headcount ?? 0) > 0);
  const dayStatus = {
    filled: blocks[0]?.filled ?? 0,
    planned: blocks[0]?.headcount ?? 0,
  };
  const nightStatus = {
    filled: blocks[1]?.filled ?? 0,
    planned: blocks[1]?.headcount ?? 0,
  };

  const [clearWorkload, { loading }] = useMutation(CLEAR_WORKLOAD_MUTATION, {
    onCompleted: () => {
      createMessage('success', 'Workload has been cleared.');
      navigate(buildCloseAllDialogsUrl());
    },
    onError: () => {
      createMessage('error', 'Failed to clear workload. Please try again.');
    },
    refetchQueries: ['ShiftsCountByDay', 'ShiftGapsByDay'],
  });

  const handleClear = useCallback(() => {
    if (!queryRange) return;

    clearWorkload({
      variables: {
        organizationUnitId: organizationUnitId!,
        roleId: roleId!,
        startsAt: queryRange.startsAt,
        endsAt: queryRange.endsAt,
      },
    });
  }, [clearWorkload, organizationUnitId, roleId, queryRange]);

  useEffect(() => {
    onStateChange({
      canSave,
      loading,
      hasExistingBlocks,
      onClear: handleClear,
    });
  }, [canSave, loading, hasExistingBlocks, handleClear, onStateChange]);

  const handleFinish = () => {
    if (boundaryHour === null) return;

    const shiftGapIdsToCancel = manualShiftGaps
      .map((gap) => gap.id)
      .filter((id) => !shiftGapIdsToKeep.has(id));

    const dayHeadcount = form.getFieldValue('dayHeadcount') ?? 0;
    const nightHeadcount = form.getFieldValue('nightHeadcount') ?? 0;

    onContinue({
      organizationUnitId: organizationUnitId!,
      roleId: roleId!,
      boundaryHour,
      dayHeadcount,
      nightHeadcount,
      shiftGapIdsToCancel,
    });
  };

  const hasBothHours = boundaryHour !== null && nightHour !== null;
  const dayLabel = hasBothHours
    ? `Day ${formatHour(boundaryHour)} - ${formatHour(nightHour)}`
    : '';
  const nightLabel = hasBothHours
    ? `Night ${formatHour(nightHour)} - ${formatHour(boundaryHour)}`
    : '';

  const dataSource = [
    {
      key: 'day',
      hours: (
        <Gap>
          <Icon icon="sun" color="harvestGold" />
          <span>{dayLabel}</span>
        </Gap>
      ),
      needs: (
        <Form.Item name="dayHeadcount" noStyle initialValue={0}>
          <InputNumber min={dayStatus.filled} max={100} />
        </Form.Item>
      ),
      status: `${dayStatus.filled}/${dayStatus.planned} filled`,
    },
    {
      key: 'night',
      hours: (
        <Gap>
          <Icon icon="moon" color="skyDarker" />
          <span>{nightLabel}</span>
        </Gap>
      ),
      needs: (
        <Form.Item name="nightHeadcount" noStyle initialValue={0}>
          <InputNumber min={nightStatus.filled} max={100} />
        </Form.Item>
      ),
      status: `${nightStatus.filled}/${nightStatus.planned} filled`,
    },
  ];

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onValuesChange={(changed) => {
        if ('organizationUnitId' in changed) {
          setOrganizationUnitId(changed.organizationUnitId);
          setRoleId(null);
          form.setFieldsValue({
            roleId: undefined,
            dayHeadcount: 0,
            nightHeadcount: 0,
          });
          setBoundaryHour(null);
          setShiftGapIdsToKeep(new Set());
        }
        if ('roleId' in changed) {
          setRoleId(changed.roleId);
          setShiftGapIdsToKeep(new Set());
        }
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <OrganizationUnitSelect />
        </Col>
        <Col span={12}>
          <RoleSelect roles={rolesData?.roles} loading={rolesLoading} />
        </Col>
      </Row>
      {organizationUnitId && !configCheckComplete ? (
        <LoadingState size="sm" align="center" />
      ) : (
        <>
          {showWarning && <NoShiftConfigsWarning date={date} />}
          {!showWarning && hasUnitAndRole && manualShiftGaps.length > 0 && (
            <ConflictShiftsBanner
              shiftGaps={manualShiftGaps}
              selectedIds={shiftGapIdsToKeep}
              onSelectionChange={setShiftGapIdsToKeep}
            />
          )}
          {!showWarning && hasUnitAndRole && (
            <Table
              columns={COLUMNS}
              dataSource={dataSource}
              pagination={false}
              loading={boundaryLoading || workloadLoading}
              size="middle"
            />
          )}
          {!showWarning && hasUnitAndRole && shiftConfigs.length > 0 && (
            <ShiftConfigsInfo
              shiftConfigs={shiftConfigs}
              boundaryHour={boundaryHour}
            />
          )}
        </>
      )}
    </Form>
  );
}

function formatHour(hour: number): string {
  // dayjs has no objectSupport, so the old `{ hour }` construction is an
  // Invalid Date. Build the time from now instead — no object construction.
  return toHumanTimeFormat(datetime().startOf('day').hour(hour));
}

export function utcDateAtHour(
  date: string | undefined,
  hour: number,
  addDays = 0
) {
  return datetime.utc(date).add(addDays, 'day').startOf('day').hour(hour);
}
